import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import AlertBox from "components/partials/alert-box/alert-box";

import { type AlertColor } from "ui/alert";
import { Fade } from "ui/fade";

import { useDisclosure } from "hooks/use-disclosure";

export type AlertMessage = {
  status: AlertColor;
  title: string;
  message?: string;
  button?: { path: string; label: string; variant?: "primary" | "secondary"; onClick?: () => void };
  link?: {
    label: string;
    route: string;
  };
};

const Flash = ({ sx }: { sx?: SxProps<Theme> }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const flashRef = useRef<HTMLDivElement>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [flashMessages, setFlashMessages] = useState<AlertMessage[] | undefined>();

  function createFlashMessages() {
    // Note: Does this actually need to allow for an array of messages?
    if (location.state && (location.state as any).flash) {
      Object.entries<AlertMessage | AlertMessage[]>((location.state as any).flash).flatMap(([_, messages]) =>
        setFlashMessages(arrayify(messages)),
      );
    }
  }

  function showFlashMessages() {
    createFlashMessages();
    onOpen();
    setTimeout(() => {
      // onOpen() needs to fire before scrolling
      flashRef.current?.scrollIntoView({ block: "end", inline: "nearest", behavior: "smooth" });
    });
  }

  function dismissFlashMessages() {
    // Note: This clears all messages, not just one, but we don't currently show any arrays of messages.
    setFlashMessages(undefined);
  }

  function handleClose() {
    onClose();
    setTimeout(() => {
      navigate(".", { replace: true, state: undefined });
    }, 150);
  }

  useEffect(
    function setOrClearFlashMessages() {
      if (location?.state && location.state.flash && Object.entries(location.state.flash).length > 0) {
        showFlashMessages();
      }
      return () => {
        dismissFlashMessages();
      };
    },
    // eslint-disable-next-line
    [location.state],
  );

  if (!flashMessages) {
    return null;
  }

  return (
    <Fade in={isOpen}>
      <div ref={flashRef} style={{ marginBottom: "20px" }}>
        {flashMessages.map((flash, index) => (
          <AlertBox alert={flash} handleClose={handleClose} key={index} sx={sx} />
        ))}
      </div>
    </Fade>
  );
};

export default Flash;

function arrayify<T>(thing: T | T[]): T[] {
  return Array.isArray(thing) ? [...thing] : [thing];
}
